import type { Dayjs } from 'dayjs'

import { appSelectors } from '@appStore/reducers/app.ts'
import { logger } from '@nanostores/logger'
import Badge from 'components/atoms/Badge'
import { ReactDatePicker } from 'components/atoms/ReactDatePicker/ReactDatePicker'
import { CustomTip } from 'components/atoms/tooltipCustom'
import { $isScheduledShown } from 'features/scheduled-messages/store'
import { atom } from 'nanostores'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import InputMask from 'react-input-mask'
import { useParams } from 'react-router-dom'
import { appDay } from 'shared/day'
import { useAppSelector } from 'shared/hooks'
import { LoadingButton, StackIcon } from 'shared/ui'
import { closeOnBackdropClick, useDialogPolyfill } from 'shared/ui/Dialog/hooks'

import { api } from '../../api'
import css from './TimePicker.module.css'

interface State {
	id?: string
	text: string
	time: Dayjs
}

type TimeValue = [number, number]

export const $timePickerState = atom<null | State>(null)

// Debug
if (import.meta.env.DEV) {
	// $timePickerState.set({
	// 	text: '',
	// 	time: appDay().add(3, 'hour').set('minute', 0)
	// })

	logger({
		$timePickerState
	})
}

function resetForm() {
	const form = document.getElementById('message-form') as HTMLFormElement | null

	form?.reset()
}

const parseTime = (time: string) => time.split(':').map(Number) as TimeValue

const isValidHours = (hours: number) =>
	!Number.isNaN(hours) && hours >= 0 && hours <= 23
const isValidMinutes = (minutes: number) =>
	!Number.isNaN(minutes) && minutes >= 0 && minutes <= 59

function isValidTime(time: TimeValue) {
	const [hours, minutes] = time
	return isValidHours(hours) && isValidMinutes(minutes)
}

const resetState = () => $timePickerState.set(null)

TimePicker.open = (text = '') => {
	$timePickerState.set({ text, time: appDay().add(1, 'day') })
}

export function TimePicker() {
	const { conversation = '' } = useParams<{ conversation: string }>()

	const [send, { isLoading }] = api.useScheduleMessageMutation()
	const [reschedule, { isLoading: rescheduleLoading }] =
		api.useUpdateScheduledMessageMutation()

	const appProfile = useAppSelector(appSelectors.appProfile)
	const isProPlanAccessible = useAppSelector(appSelectors.isPro)

	const shouldBlock = isProPlanAccessible === false || appProfile.isTrial

	const errorID = useId()

	const [date, setDate] = useState(() => new Date()),
		[error, setError] = useState(''),
		[time, setTime] = useState('__:__')

	const dialogRef = useRef<HTMLDialogElement | null>(null),
		formRef = useRef<HTMLFormElement | null>(null)

	useDialogPolyfill(dialogRef)

	useEffect(
		() =>
			$timePickerState.listen(state => {
				if (state === null) {
					dialogRef.current?.close()
					formRef.current?.reset()
					return
				}

				setDate(state.time.toDate())
				const formatted = state.time.format('HH:mm') ?? ''
				console.log('formatted', formatted)
				// setTime(formatted)
				dialogRef.current?.showModal()
				/**
				 * ! Stupid 'react-input-mask call onChange event on every render of parent,
				 * so if we don't change queue it will be replaced with previous value on second modal open
				 */
				queueMicrotask(() => setTime(formatted))
			}),
		[]
	)

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault()
		const form = event.currentTarget
		const state = $timePickerState.get()

		const parsedTime = parseTime(time)

		if (!isValidTime(parsedTime)) {
			return setError('Invalid time format')
		}

		const [hours, minutes] = parsedTime
		const finalTime = appDay(date).set('hour', hours).set('minute', minutes)

		const minTime = appDay().add(10, 'minutes')
		if (!finalTime.isAfter(minTime)) {
			return setError('Scheduled time must be in the future')
		}
		setError('')

		const common = {
			text: state?.text ?? '',
			time: finalTime.toISOString()
		}

		if (state?.id) {
			await reschedule({
				id: state.id,
				...common
			})
		} else {
			await send({
				conversation_id: conversation,
				...common
			})
		}

		$isScheduledShown.set(true)
		resetForm()
		form.reset()
	}

	return (
		<dialog
			className={css.root}
			onClick={closeOnBackdropClick}
			onClose={resetState}
			ref={dialogRef}
		>
			<form
				className={css.wrapper}
				onReset={() => {
					setTime('')
					setError('')
					setDate(() => new Date())
					dialogRef.current?.close()
				}}
				onSubmit={handleSubmit}
				ref={formRef}
			>
				<h2 className={css.title}>Send this message on...</h2>

				<div className={css.inputs}>
					<ReactDatePicker
						className={css.date}
						minDate={new Date()}
						onChange={date => date instanceof Date && setDate(date)}
						selected={date}
					/>
					at
					<div className={css.timeWrapper}>
						<InputMask
							aria-errormessage={errorID}
							// @ts-ignore eslint-disable-next-line Recheck in React 19
							autofocus='true'
							className={css.time}
							mask='99:99'
							onChange={(event: any) => {
								console.log('onChange', event.target.value)
								setTime(event.target.value)
							}}
							value={time}
						/>

						<StackIcon className={css.clock} icon='clock' width={16} />
					</div>
				</div>

				<p className={css.error} id={errorID}>
					{error}
				</p>

				<div className={css.actions}>
					<button className={css.reset} type='reset'>
						Cancel
					</button>
						{(() => {
							const button = (
								<LoadingButton
									className={css.button}
									disabled={shouldBlock}
									isLoading={isLoading || rescheduleLoading}
									type='submit'
								>
									Schedule
								</LoadingButton>
							)

							return shouldBlock ? (
								<CustomTip
									arrow
									placement='top'
									slotProps={{
										popper: { disablePortal: true, sx: { zIndex: 100000 } },
										tooltip: { sx: { filter: 'none' } }
									}}
									title='Upgrade your plan to Pro'
								>
									<div style={{ display: 'inline-flex', position: 'relative' }}>
										{button}
										{shouldBlock && <Badge />}
									</div>
								</CustomTip>
							) : (
								button
							)
						})()}
				</div>
			</form>
		</dialog>
	)
}
